<template>
    <div class="page">
        <div class="head">
            <div>
                <div class="page-title">Peer Devices List</div>
                <div class="page-sub">Manage peer devices and monitor real-time bandwidth</div>
            </div>
            <div class="page-actions">
                <el-button size="small" @click="onExport">
                    <i class="el-icon-download" />
                    <span>Export CSV</span>
                </el-button>

            </div>
        </div>

        <div class="kpis">
            <div class="kpi-card">
                <div class="kpi-top">
                    <div class="kpi-icon kpi-blue"><i class="el-icon-connection" /></div>
                </div>
                <div class="kpi-label">Total Proxies</div>
                <div class="kpi-value">{{ kpi.totalProxies }}</div>
            </div>

            <div class="kpi-card">
                <div class="kpi-top">
                    <div class="kpi-icon kpi-purple"><i class="el-icon-data-analysis" /></div>
                </div>
                <div class="kpi-label">Bandwidth Total </div>
                <div class="kpi-value">{{ kpi.bandwidthTotal }}</div>

            </div>

        </div>

        <div class="filter-bar">
            <el-input v-model="filters.keyword" size="small" clearable placeholder="Search by Device ID..."
                prefix-icon="el-icon-search" class="filter-pill filter-keyword" @keyup.enter.native="onApply" />

            <div class="filter-right">
                <el-select v-model="filters.country" size="small" clearable filterable placeholder="Country: All"
                    class="filter-pill" @change="onApply">
                    <el-option v-for="c in countryOptions" :key="c.code" :label="c.name" :value="c.code">
                        <span>
                            <img :src="flagUrl(c.code.toLowerCase())" style="width:16px;height:11px;margin-right:6px;vertical-align:middle;border-radius:2px;">
                            {{ c.name }}
                        </span>
                    </el-option>
                </el-select>

                <el-select v-model="filters.status" size="small" clearable placeholder="Status: All" class="filter-pill"
                    @change="onApply">
                    <el-option label="Active" value="active" />
                    <el-option label="Inactive" value="inactive" />
                </el-select>

<el-button size="small" type="primary" @click="onApply">Filter</el-button>
                <el-button size="small" type="text" class="reset" @click="onReset">Reset</el-button>
            </div>
        </div>

        <div class="table-card">
            <el-table ref="table" v-loading="loading" :data="rows" :height="tableHeight" class="devices-table"
                style="width: 100%">
                <el-table-column label="Device ID" min-width="200">
                    <template slot-scope="scope">
                        <div class="id-cell">
                            <span class="cell-strong">{{ scope.row.guid || scope.row.id }}</span>
                            <el-button type="text" class="icon-btn"
                                @click="copyText(scope.row.guid || String(scope.row.id || ''))">
                                <i class="el-icon-document-copy" />
                            </el-button>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="IP Address" min-width="180">
                    <template slot-scope="scope">
                        <div class="ip-cell">
                            <span class="mono">{{ scope.row.ip || '-' }}</span>
                            <el-button v-if="scope.row.ip" type="text" class="icon-btn"
                                @click="copyText(scope.row.ip)">
                                <i class="el-icon-document-copy" />
                            </el-button>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="Country" min-width="200">
                    <template slot-scope="scope">
                        <div class="country-cell">
                            <img v-if="scope.row.country" class="flag" :src="flagUrl(scope.row.country)"
                                :alt="scope.row.country">
                            <span class="country-name">{{ countryName(scope.row.country) }}</span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="Status" width="150">
                    <template slot-scope="scope">
                        <span class="badge" :class="badgeClass(scope.row)">{{ badgeLabel(scope.row) }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="Relay" width="150" align="center">
                    <template slot-scope="scope">
                        <span class="pill">{{ scope.row.relay_id || '-' }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="Total Shared" min-width="160">
                    <template slot-scope="scope">
                        <span>{{ fmtBytes(Number(scope.row.in || 0) + Number(scope.row.out || 0)) }}</span>
                    </template>
                </el-table-column>

                <el-table-column width="70" align="right">
                    <template slot-scope="scope">
                        <el-dropdown trigger="click">
                            <el-button type="text" class="more-btn">
                                <i class="el-icon-more" />
                            </el-button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item @click.native="copyText(scope.row.guid || String(scope.row.id || ''))">Copy
                                    GUID</el-dropdown-item>
                                <el-dropdown-item @click.native="copyText(scope.row.ip || '')">Copy
                                    IP</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </template>
                </el-table-column>
            </el-table>

            <div class="footer">
                <div class="footer-left">
                    Showing <span class="footer-strong">{{ showingFrom }}</span> to <span class="footer-strong">{{
                        showingTo
                        }}</span> of <span class="footer-strong">{{ total }}</span> results
                </div>
                <div class="footer-right">
                    <el-pagination :current-page="currentPage" :page-size="size" :page-sizes="[10, 20, 50]"
                        :total="total" layout="sizes, prev, pager, next" @size-change="onSizeChange"
                        @current-change="onCurrentChange" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getAdminNodes } from '../../api/peers'
import { getAdminProxySummaryNode } from '../../api/proxyAdmin'
import { getAdminBandwidthSummaryNode } from '../../api/overview'
import { COUNTRIES } from '../../constants/countries'

export default {
    name: 'PeerDevicesView',
    data() {
        return {
            loading: false,
            rows: [],
            total: 0,
            size: 20,
            page: 1,
            tableHeight: 420,
            maxMbps: 30,
            kpi: {
                totalProxies: '--',
                bandwidthTotal: '--'
            },
            filters: {
                keyword: '',
                status: '',
                country: '',
                sort_by: ''
            },
            countryOptions: COUNTRIES
        }
    },
    computed: {
        currentPage() {
            return this.page
        },
        showingFrom() {
            if (!this.total) return 0
            return (this.page - 1) * this.size + 1
        },
        showingTo() {
            return Math.min(this.page * this.size, this.total)
        }
    },
    mounted() {
        this.fetchRows()
        this.fetchKpis()
        this.$nextTick(this.calcTableHeight)
        window.addEventListener('resize', this.calcTableHeight, false)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.calcTableHeight, false)
    },
    methods: {
        calcTableHeight() {
            try {
                const el = this.$refs.table && this.$refs.table.$el ? this.$refs.table.$el : null
                if (!el) return
                const rect = el.getBoundingClientRect()
                const footer = this.$el ? this.$el.querySelector('.footer') : null
                const footerH = footer ? footer.getBoundingClientRect().height : 0
                const paddingBottom = 24
                const h = Math.floor(window.innerHeight - rect.top - footerH - paddingBottom)
                this.tableHeight = Math.max(260, h)
            } catch (e) { }
        },
        async copyText(text) {
            const t = String(text || '')
            if (!t) return
            try {
                if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(t)
                } else {
                    const el = document.createElement('textarea')
                    el.value = t
                    el.setAttribute('readonly', '')
                    el.style.position = 'absolute'
                    el.style.left = '-9999px'
                    document.body.appendChild(el)
                    el.select()
                    document.execCommand('copy')
                    document.body.removeChild(el)
                }
                this.$message && this.$message.success('Copied')
            } catch (e) {
                this.$message && this.$message.error('Copy failed')
            }
        },
        onExport() {
            this.$message && this.$message.info('Export (demo)')
        },
        flagUrl(code) {
            const c = String(code || '').trim().toLowerCase()
            return c ? `https://flagcdn.com/w40/${c}.png` : ''
        },
        countryName(code) {
            const c = String(code || '').toUpperCase()
            const map = { VN: 'Vietnam', US: 'United States', DE: 'Germany', JP: 'Japan', SG: 'Singapore', BR: 'Brazil' }
            return map[c] || c || '-'
        },
        badgeLabel(row) {
            return (row && row.status === 'active') ? 'Active' : (row && row.status || 'Inactive')
        },
        badgeClass(row) {
            return (row && row.status === 'active') ? 'is-online' : 'is-offline'
        },
        // in/out are bytes; convert to Mbps for UI bar
        currentMbps(row) {
            const bytes = Number((row && row.in) || 0) + Number((row && row.out) || 0)
            return bytes / 1024 / 1024 * 8
        },
        fmtMbps(n) {
            return Number(n || 0).toFixed(1)
        },
        bwPercent(row) {
            const p = (this.currentMbps(row) / (this.maxMbps || 1)) * 100
            return Math.max(0, Math.min(100, Math.round(p)))
        },
        bwColor(row) {
            const p = this.bwPercent(row)
            if (p >= 90) return '#ef4444'
            if (p >= 70) return '#f59e0b'
            return '#135bec'
        },
        bwTextClass(row) {
            return this.bwPercent(row) >= 90 ? 'txt-red' : ''
        },
        fmtBytes(n) {
            const num = Number(n || 0)
            const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
            let v = num
            let i = 0
            while (v >= 1024 && i < units.length - 1) {
                v = v / 1024
                i++
            }
            return (i === 0 ? String(Math.round(v)) : v.toFixed(2)) + ' ' + units[i]
        },
        async fetchKpis() {
            try {
                const [proxySummary, bwSummary] = await Promise.all([
                    getAdminProxySummaryNode(),
                    getAdminBandwidthSummaryNode()
                ])
                const total = proxySummary && (proxySummary.total !== undefined ? proxySummary.total : (proxySummary.data && proxySummary.data.total))
                this.kpi.totalProxies = (total == null) ? '--' : String(total)
                const bw = bwSummary && (bwSummary.total !== undefined ? bwSummary.total : (bwSummary.data && bwSummary.data.total))
                this.kpi.bandwidthTotal = (bw == null) ? '--' : this.fmtBytes(bw)
            } catch (e) { }
        },
        async fetchRows() {
            this.loading = true
            try {
                const res = await getAdminNodes({
                    size: this.size,
                    page: this.page,
                    keyword: this.filters.keyword,
                    status: this.filters.status,
                    country: this.filters.country
                })
                const d = res && res.data
                this.rows = (d && d.items) ? d.items : []
                this.total = (d && typeof d.total === 'number') ? d.total : this.rows.length
            } catch (e) {
                this.$message && this.$message.error('Không tải được danh sách node')
            } finally {
                this.loading = false
                this.$nextTick(this.calcTableHeight)
                this.fetchKpis()
            }
        },
        onApply() {
            this.page = 1
            this.fetchRows()
        },
        onReset() {
            this.filters = { keyword: '', status: '', country: '', sort_by: '' }
            this.page = 1
            this.fetchRows()
        },
        onSizeChange(size) {
            this.size = size
            this.page = 1
            this.fetchRows()
        },
        onCurrentChange(page) {
            this.page = Number(page || 1)
            this.fetchRows()
        }
    }
}
</script>

<style scoped>
.page {
    padding: 16px;
}

.head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    align-items: center;
}

.page-title {
    font-size: 28px;
    font-weight: 900;
    color: #ffffff;
}

.page-sub {
    margin-top: 6px;
    color: #92a4c9;
}

.page-actions {
    display: flex;
    gap: 10px;
}

.kpis {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 14px;
}

.kpi-card {
    background: #111722;
    border: 1px solid #324467;
    border-radius: 12px;
    padding: 14px;
}

.kpi-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.kpi-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.kpi-blue {
    background: rgba(59, 130, 246, 0.18);
    color: #60a5fa;
}

.kpi-purple {
    background: rgba(168, 85, 247, 0.18);
    color: #c084fc;
}

.kpi-amber {
    background: rgba(245, 158, 11, 0.18);
    color: #fbbf24;
}

.kpi-label {
    margin-top: 10px;
    color: #92a4c9;
    font-weight: 600;
}

.kpi-value {
    margin-top: 6px;
    font-size: 22px;
    font-weight: 800;
}

.kpi-sub {
    margin-top: 4px;
    font-size: 12px;
    color: #92a4c9;
}

.filter-bar {
    background: #161e2c;
    border: 1px solid #324467;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 14px;
    overflow-x: auto;
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: nowrap;
    min-width: 980px;
}

.filter-right {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: nowrap;
}

.filter-pill {
    min-width: 160px;
}

.filter-keyword {
    min-width: 340px;
    flex: 1;
}

.reset {
    color: #135bec;
    font-weight: 700;
}

.table-card {
    background: #111722;
    border: 1px solid #324467;
    border-radius: 12px;
    overflow: hidden;
}

.cell-strong {
    font-weight: 800;
}

.muted {
    color: #92a4c9;
    font-size: 12px;
}

.bw-wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.bw-top {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
}

.txt-red {
    color: #ef4444;
    font-weight: 700;
}

.country-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.flag {
    width: 20px;
    height: 14px;
    border-radius: 2px;
}

.country-name {
    color: #cbd5e1;
}

.id-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.ip-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.icon-btn {
    padding: 0 6px;
    color: #94a3b8;
}

.icon-btn:hover {
    color: #ffffff;
}

.badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.badge.is-online {
    background: rgba(34, 197, 94, 0.18);
    color: #86efac;
}

.badge.is-offline {
    background: rgba(239, 68, 68, 0.18);
    color: #fca5a5;
}

.pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    background: rgba(59, 130, 246, 0.18);
    color: #93c5fd;
}

.more-btn {
    color: #94a3b8;
}

.more-btn:hover {
    color: #ffffff;
    background: #324467;
}

.footer {
    border-top: 1px solid #324467;
    background: #161e2c;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}

.footer-left {
    color: #92a4c9;
    font-size: 12px;
}

.footer-strong {
    color: #ffffff;
    font-weight: 800;
}

.footer-right /deep/ .el-pager li {
    background: transparent;
    color: #92a4c9;
}

.footer-right /deep/ .el-pager li.active {
    color: #ffffff;
    background: #135bec;
    border-radius: 8px;
}

.footer-right /deep/ .btn-prev,
.footer-right /deep/ .btn-next {
    background: #232f48;
    color: #ffffff;
    border-radius: 8px;
}

@media (max-width: 900px) {
    .kpis {
        grid-template-columns: 1fr;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/peers/index.vue